<template>
  <Teleport to="body">
    <Transition name="uab-slide">
      <div v-if="updateAvailable" class="uab-banner" role="alert" aria-live="polite">
        <div class="uab-banner__inner">
          <div class="uab-banner__icon" aria-hidden="true">
            <v-icon icon="mdi-rocket-launch-outline" size="18" />
          </div>

          <div class="uab-banner__body">
            <p class="uab-banner__title">A new version of Yandle Portal is ready.</p>
            <button v-if="notesList.length" type="button" class="uab-banner__toggle" @click="dialogOpen = true">
              See what's new
              <v-icon icon="mdi-chevron-right" size="14" />
            </button>
          </div>

          <button type="button" class="uab-banner__refresh" @click="hardRefresh">
            <v-icon icon="mdi-refresh" size="16" />
            Refresh now
          </button>
        </div>
      </div>
    </Transition>

    <!-- Reuses the app's own dialog design language (App.vue's global
         .user-dialog-card / .dialog-header / .dialog-body / .dialog-footer)
         rather than a bespoke look, so it feels like part of the product
         instead of a bolted-on notice. -->
    <v-dialog v-model="dialogOpen" max-width="520" scrollable content-class="uab-dialog-root">
      <v-card rounded="xl" class="user-dialog-card">
        <div class="dialog-header">
          <div class="uab-dialog__header-main">
            <div class="dialog-header__icon" aria-hidden="true">
              <v-icon icon="mdi-rocket-launch-outline" size="20" />
            </div>
            <div class="uab-dialog__head-copy">
              <span class="dialog-header__title">What's new</span>
              <span v-if="versionTitle" class="uab-dialog__subtitle">{{ versionTitle }}</span>
            </div>
          </div>
          <v-btn
            icon
            variant="flat"
            size="small"
            class="dialog-close-btn"
            rounded="lg"
            @click="dialogOpen = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="dialog-body">
          <ul class="uab-dialog__notes">
            <li v-for="(line, i) in notesList" :key="i">{{ line }}</li>
          </ul>
        </v-card-text>

        <v-card-actions class="dialog-footer">
          <v-btn variant="text" class="dialog-btn--muted" @click="dialogOpen = false">
            Maybe later
          </v-btn>
          <v-btn variant="flat" class="btn-primary-cta" prepend-icon="mdi-refresh" @click="hardRefresh">
            Refresh now
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </Teleport>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { updateAvailable, latestVersionInfo, startVersionCheck, hardRefresh } from '@/services/versionCheck'

defineOptions({ name: 'UpdateAvailableBanner' })

const dialogOpen = ref(false)

const versionTitle = computed(() => latestVersionInfo.value?.title || '')

// Commit bodies hand-wrap a single bullet across several lines (a leading
// "- " starts a new point, an indented continuation line has neither) --
// split naively on "\n" and each wrapped line becomes its own bullet, so
// this reassembles them into the points they actually were before display.
const notesList = computed(() => {
  const raw = latestVersionInfo.value?.notes || ''
  const items = []
  for (const line of raw.split('\n')) {
    if (!line.trim()) continue
    if (/^-\s+/.test(line)) {
      items.push(line.replace(/^-\s+/, '').trim())
    } else if (items.length) {
      items[items.length - 1] += ' ' + line.trim()
    } else {
      items.push(line.trim())
    }
  }
  return items
})

onMounted(() => {
  startVersionCheck()
})
</script>

<style scoped>
.uab-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3000;
  display: flex;
  justify-content: center;
  padding: 10px 16px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}

.uab-banner__inner {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 920px;
}

.uab-banner__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.22);
  color: #a5b4fc;
}

.uab-banner__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.uab-banner__title {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.4;
  color: #f8fafc;
}

.uab-banner__toggle {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.75rem;
  font-weight: 500;
  color: #93c5fd;
  cursor: pointer;
}

.uab-banner__toggle:hover {
  color: #bfdbfe;
  text-decoration: underline;
}

.uab-banner__refresh {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 7px 14px;
  border: none;
  border-radius: 8px;
  background: #6366f1;
  color: #ffffff;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
}

.uab-banner__refresh:hover {
  background: #4f46e5;
}

.uab-banner__refresh:active {
  transform: scale(0.97);
}

.uab-slide-enter-active,
.uab-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.uab-slide-enter-from,
.uab-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

@media (max-width: 640px) {
  .uab-banner__inner {
    flex-wrap: wrap;
  }

  .uab-banner__refresh {
    width: 100%;
    justify-content: center;
  }
}

/* ── What's new dialog — layout only; colors/surfaces come from the
     global .user-dialog-card family in App.vue ── */
.uab-dialog__header-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.uab-dialog__head-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.uab-dialog__subtitle {
  font-size: 0.75rem;
  color: var(--y-text-muted);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.uab-dialog__notes {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--y-dialog-body-text);
}
</style>
